import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook for managing media player state
 * 
 * Provides:
 * - Play/pause state management
 * - Video/audio ref management
 * - Unified control functions
 * - Event handlers for media events
 */

export const useMediaPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const togglePlayPause = useCallback((mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    
    if (ref.current) {
      if (isPlaying) {
        ref.current.pause();
      } else {
        ref.current.play();
      }
    }
  }, [isPlaying]);

  const play = useCallback((mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    if (ref.current) {
      ref.current.play();
    }
  }, []);

  const pause = useCallback((mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    if (ref.current) {
      ref.current.pause();
    }
  }, []);

  const seek = useCallback((time, mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    if (ref.current) {
      ref.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const setVolumeLevel = useCallback((level, mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    if (ref.current) {
      ref.current.volume = level;
      setVolume(level);
    }
  }, []);

  const toggleMute = useCallback((mediaType = 'video') => {
    const ref = mediaType === 'audio' ? audioRef : videoRef;
    if (ref.current) {
      ref.current.muted = !ref.current.muted;
      setIsMuted(ref.current.muted);
    }
  }, []);

  // Event handlers
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleTimeUpdate = useCallback((e) => {
    setCurrentTime(e.target.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback((e) => {
    setDuration(e.target.duration);
  }, []);

  const handleVolumeChange = useCallback((e) => {
    setVolume(e.target.volume);
    setIsMuted(e.target.muted);
  }, []);

  return {
    // State
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    
    // Refs
    videoRef,
    audioRef,
    
    // Controls
    togglePlayPause,
    play,
    pause,
    seek,
    setVolumeLevel,
    toggleMute,
    
    // Event handlers
    handlePlay,
    handlePause,
    handleEnded,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleVolumeChange,
    
    // Setters (for external control)
    setIsPlaying,
    setCurrentTime,
    setDuration
  };
};
